/**
 * PSSP LABS — "PSSP Precision" theme
 * process-timeline.js — <pssp-process> scroll-driven process timeline.
 *
 * Markup contract (sections/process-timeline.liquid):
 *   <pssp-process class="process-timeline__layout">
 *     <div class="process-timeline__rail">
 *       … <button data-process-target="ProcessPanel-{id}-{n}">…</button> …
 *     </div>
 *     <ol class="process-timeline__panels">
 *       <li class="process-timeline__panel" id="ProcessPanel-{id}-{n}">…</li> × n
 *     </ol>
 *   </pssp-process>
 *
 * Behavior:
 * - Lazy init: scroll tracking starts only when the section nears the
 *   viewport (PSSPTheme.onVisible). Rail clicks work immediately.
 * - An IntersectionObserver band (35%→45% down the viewport) decides the
 *   active panel → matching rail button gets aria-current="step", the host
 *   --progress custom property updates (drives the vertical progress bar),
 *   and the active panel gets .is-active (mobile dot + desktop emphasis).
 * - Rail clicks scroll the target panel into view — smooth only when
 *   PSSPTheme.motionOK().
 * - Panel videos: when motion is allowed, native controls are stripped and
 *   only the active panel's muted video plays; everything pauses while the
 *   tab is hidden. With motion off (or no JS) videos keep their controls
 *   and never autoplay.
 * - Theme editor: shopify:block:select scrolls the selected step into view.
 * - Every listener/observer is removed in disconnectedCallback().
 */

if (!customElements.get('pssp-process')) {
  customElements.define(
    'pssp-process',
    class extends HTMLElement {
      connectedCallback() {
        this._panels = Array.prototype.slice.call(
          this.querySelectorAll('.process-timeline__panel')
        );
        if (this._panels.length === 0) return;

        this._buttons = Array.prototype.slice.call(
          this.querySelectorAll('[data-process-target]')
        );
        this._activeIndex = -1;
        this._io = null;

        /* Rail navigation — delegated, active before lazy init. */
        this._onClick = (event) => {
          const btn = event.target.closest('[data-process-target]');
          if (!btn || !this.contains(btn)) return;
          const panel = document.getElementById(btn.getAttribute('data-process-target'));
          if (!panel) return;
          panel.scrollIntoView({
            behavior: PSSPTheme.motionOK() ? 'smooth' : 'auto',
            block: 'start'
          });
        };
        this.addEventListener('click', this._onClick);

        /* Theme editor: bring the selected step into view. */
        this._onBlockSelect = (event) => {
          if (!this.contains(event.target)) return;
          const panel = event.target.closest('.process-timeline__panel');
          if (!panel) return;
          panel.scrollIntoView({ behavior: 'auto', block: 'center' });
          this._setActive(this._panels.indexOf(panel));
        };
        document.addEventListener('shopify:block:select', this._onBlockSelect);

        /* Pause every video while the tab is hidden; resume on return. */
        this._onVisibility = () => {
          if (document.hidden) {
            this._eachVideo((video) => video.pause());
          } else {
            this._syncVideos();
          }
        };
        document.addEventListener('visibilitychange', this._onVisibility);

        /* Scroll tracking starts when the section nears the viewport. */
        this._stopVisible = PSSPTheme.onVisible(this, () => this._init());
      }

      disconnectedCallback() {
        if (this._stopVisible) this._stopVisible();
        if (this._io) this._io.disconnect();
        if (this._onClick) this.removeEventListener('click', this._onClick);
        if (this._onBlockSelect) {
          document.removeEventListener('shopify:block:select', this._onBlockSelect);
        }
        if (this._onVisibility) {
          document.removeEventListener('visibilitychange', this._onVisibility);
        }
        this._stopVisible = null;
        this._io = null;
        this._onClick = null;
        this._onBlockSelect = null;
        this._onVisibility = null;
      }

      _init() {
        if (this._io || !this.isConnected) return;

        /* With motion allowed, videos become ambient loops: strip the
           native controls and let the active-step logic drive playback.
           iOS requires playsinline for scripted inline playback. */
        this._eachVideo((video) => {
          video.setAttribute('playsinline', '');
          video.muted = true;
          if (PSSPTheme.motionOK()) video.removeAttribute('controls');
        });

        if (!('IntersectionObserver' in window)) {
          this._setActive(0);
          return;
        }

        /* Slim horizontal band 35%→45% down the viewport: whichever panel
           crosses it is the active step. */
        this._io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              this._setActive(this._panels.indexOf(entry.target));
            });
          },
          { rootMargin: '-35% 0px -55% 0px', threshold: 0 }
        );
        this._panels.forEach((panel) => this._io.observe(panel));

        if (this._activeIndex === -1) this._setActive(0);
      }

      _setActive(index) {
        if (index < 0 || index === this._activeIndex) return;
        this._activeIndex = index;

        this._panels.forEach((panel, i) => {
          panel.classList.toggle('is-active', i === index);
        });

        this._buttons.forEach((btn, i) => {
          if (i === index) {
            btn.setAttribute('aria-current', 'step');
          } else {
            btn.removeAttribute('aria-current');
          }
        });

        /* 0..1 → scaleY of the rail progress bar (base marker: step done). */
        const progress = (index + 1) / this._panels.length;
        this.style.setProperty('--progress', progress.toFixed(4));

        this._syncVideos();
      }

      /** Only the active panel's video plays, and only when motion is OK. */
      _syncVideos() {
        const motion = PSSPTheme.motionOK();
        this._panels.forEach((panel, i) => {
          const video = panel.querySelector('video');
          if (!video) return;
          if (motion && i === this._activeIndex && !document.hidden) {
            const played = video.play();
            if (played && typeof played.catch === 'function') {
              played.catch(() => {});
            }
          } else if (!video.paused) {
            video.pause();
          }
        });
      }

      _eachVideo(fn) {
        this._panels.forEach((panel) => {
          const video = panel.querySelector('video');
          if (video) fn(video);
        });
      }
    }
  );
}
